import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../lib/usePageMeta';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    { "@type": "Question", "name": "What is a word count tool?", "acceptedAnswer": { "@type": "Answer", "text": "A word count tool measures the total number of words in a piece of text, helping writers, students, and SEO professionals track content length and structure." } },
    { "@type": "Question", "name": "How does an online word count tool work?", "acceptedAnswer": { "@type": "Answer", "text": "It scans typed or pasted text, automatically counting words and often providing additional metrics like characters, sentences, and paragraphs." } },
    { "@type": "Question", "name": "Does this tool update the word count automatically?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, the word count updates in real-time as you type, delete, or paste text, showing immediate length changes." } },
    { "@type": "Question", "name": "Can I count words in a large article or document?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, you can paste long articles, blog posts, or documents, and it will instantly calculate the total word count, useful for long-form content." } },
    { "@type": "Question", "name": "Can this tool also count characters and sentences?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, in addition to words, it measures characters, sentences, and paragraphs, aiding writers in understanding content structure and length." } },
    { "@type": "Question", "name": "Is this word count tool free to use?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, it's completely free and works directly in your browser. You can type or paste text for instant counts and other metrics." } },
    { "@type": "Question", "name": "Does the tool store or save the text I enter?", "acceptedAnswer": { "@type": "Answer", "text": "No, text is processed in your browser and not stored or saved on any server, ensuring content privacy." } }
  ]
};

const faqs = [
  { q: "What is a word count tool?", a: "A word count tool measures the total number of words in a piece of text, helping writers, students, and SEO professionals track content length and structure." },
  { q: "How does an online word count tool work?", a: "It scans typed or pasted text, automatically counting words and often providing additional metrics like characters, sentences, and paragraphs." },
  { q: "Does this tool update the word count automatically?", a: "Yes, the word count updates in real-time as you type, delete, or paste text, showing immediate length changes." },
  { q: "Can I count words in a large article or document?", a: "Yes, you can paste long articles, blog posts, or documents, and it will instantly calculate the total word count, useful for long-form content." },
  { q: "Can this tool also count characters and sentences?", a: "Yes, in addition to words, it measures characters, sentences, and paragraphs, aiding writers in understanding content structure and length." },
  { q: "Is this word count tool free to use?", a: "Yes, it's completely free and works directly in your browser. You can type or paste text for instant counts and other metrics." },
  { q: "Does the tool store or save the text I enter?", a: "No, text is processed in your browser and not stored or saved on any server, ensuring content privacy." },
];

function countMetrics(text) {
  if (!text || text.trim().length === 0) {
    return { words: 0, characters: 0, sentences: 0, paragraphs: 0 };
  }
  const characters = text.length;
  const words = text.trim().split(/\s+/).filter(w => w.length > 0).length;
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0).length;
  const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim().length > 0).length || (text.trim().length > 0 ? 1 : 0);
  return { words, characters, sentences, paragraphs };
}

export default function WordCountTool() {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [metrics, setMetrics] = useState({ words: 0, characters: 0, sentences: 0, paragraphs: 0 });

  usePageMeta({
    title: 'Word Count Online for Free - Wricoo',
    description: 'Use this free word count online tool to quickly count words, characters, sentences, and paragraphs in your text. Ideal for writers, students, and SEO professionals.',
    robots: 'index, follow',
    canonical: 'https://wricoo.com/tools/word-count-online/',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema-word-count';
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('faq-schema-word-count');
      if (el) el.remove();
    };
  }, []);

  const handleTextChange = useCallback((e) => {
    const val = e.target.value;
    setText(val);
    setMetrics(countMetrics(val));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="word-count-page">

      {/* Header */}
      <header className="border-b border-blue-200 bg-blue-600 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/tools/" className="text-blue-200 hover:text-white transition"><ArrowLeft className="w-5 h-5" /></Link>
            <Link to="/" className="text-xl font-bold text-white tracking-tight">Wricoo</Link>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/about/" className="text-sm font-medium text-blue-100 hover:text-white transition">About</Link>
            <Link to="/pricing/" className="text-sm font-medium text-blue-100 hover:text-white transition">Pricing</Link>
            <Link to="/tools/" className="text-sm font-medium text-blue-100 hover:text-white transition">Tools</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white hover:bg-blue-500 hover:text-white" onClick={() => navigate('/login/')}>Sign In</Button>
            <Button size="sm" className="bg-white text-blue-600 hover:bg-blue-50" onClick={() => navigate('/register/')}>Get Started</Button>
          </div>
        </div>
      </header>

      {/* Tool Section */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h1 className="font-bold text-gray-900 leading-tight mb-6 text-center">Word Count Online for Free</h1>

          {/* Internal Links */}
          <div className="flex flex-wrap gap-3 justify-center mb-10">
            <a href="/tools/character-count-online/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">Character Count Online</a>
            <a href="/tools/case-converter-online/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">Case Converter Online</a>
            <a href="/tools/remove-extra-spaces-online/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">Remove Extra Spaces Online</a>
            <a href="/tools/ai-seo-content-brief-generator/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">AI SEO Content Brief Generator</a>
          </div>

          {/* Metrics - Words first as per PDF spec */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="metrics-display">
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600" data-testid="word-count">{metrics.words}</p>
              <p className="text-sm text-gray-600 mt-1">Words</p>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-blue-600" data-testid="char-count">{metrics.characters}</p>
              <p className="text-sm text-gray-600 mt-1">Characters</p>
            </div>
            <div className="bg-violet-50 border border-violet-100 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-violet-600" data-testid="sentence-count">{metrics.sentences}</p>
              <p className="text-sm text-gray-600 mt-1">Sentences</p>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-amber-600" data-testid="paragraph-count">{metrics.paragraphs}</p>
              <p className="text-sm text-gray-600 mt-1">Paragraphs</p>
            </div>
          </div>

          {/* Text Input */}
          <textarea
            className="w-full h-64 border border-gray-300 rounded-lg p-4 text-gray-800 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Start typing or paste your text here..."
            value={text}
            onChange={handleTextChange}
            data-testid="text-input"
          />
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-bold text-gray-900 mb-4">Pay Writers for Words They Actually Write</h2>
          <p className="text-gray-600 leading-relaxed mb-8 max-w-2xl mx-auto">Know exactly how much content your writers produce. Track word counts, organize SEO briefs, and manage writers and editors in one place without relying on spreadsheets or disconnected tools.</p>
          <Button size="lg" className="h-12 px-8" onClick={() => navigate('/pricing/')}>
            Start Your 30-Day Free Trial. No Credit Card Needed. <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="font-bold text-gray-900 mb-8">Word Count Online FAQs</h2>
          <div className="space-y-8">
            {faqs.map((faq, i) => (
              <div key={i} data-testid={`faq-item-${i}`}>
                <h3 className="text-base font-semibold text-gray-900 mb-2">Q. {faq.q}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Wricoo. All rights reserved.</p>
          <nav className="flex gap-4 text-sm text-gray-500">
            <a href="/about/" className="hover:text-gray-900 transition">About</a>
            <a href="/pricing/" className="hover:text-gray-900 transition">Pricing</a>
            <a href="/tools/" className="hover:text-gray-900 transition">Tools</a>
            <a href="/privacy-policy/" className="hover:text-gray-900 transition">Privacy Policy</a>
            <a href="/terms-and-conditions/" className="hover:text-gray-900 transition">Terms</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
